import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { FaCheck, FaPlus, FaMinus, FaClock, FaFileAlt, FaCertificate, FaShieldAlt, FaLink, FaUserTie, FaClipboardCheck, FaChartLine } from 'react-icons/fa'

const RWATokenIssuers = () => {
  const [openAccordion, setOpenAccordion] = useState<number | null>(0)

  const toggleAccordion = (index: number) => {
    setOpenAccordion(openAccordion === index ? null : index)
  }

  const trustedBy = [
    { name: 'matrixport', logo: 'M' },
    { name: 'Mountain Protocol', logo: 'MP' },
    { name: 'KITCO', logo: 'K' },
    { name: 'Uranium Digital', logo: 'UD' },
  ]

  const services = [
    {
      icon: <FaClock className="text-4xl text-accent mb-4" />,
      title: 'Real-Time Proof of Reserves',
      description: 'Reduce your RWA attestation reporting frequency from every 30 days, to every 30 seconds, with the world\'s first real-time CPA-attestation reporting system.'
    },
    {
      icon: <FaFileAlt className="text-4xl text-accent mb-4" />,
      title: 'Financial Statement Audit',
      description: 'Complete your audit to ensure your financial statements meet professional standards, enhancing stakeholder confidence and supporting compliance efforts. Ease the burden of completing your audit by working with a crypto-native team of CPAs who understand your business.'
    },
    {
      icon: <FaCertificate className="text-4xl text-accent mb-4" />,
      title: 'Stablecoin Attestations',
      description: 'Provide transparency to current and future token holders and comply with stringent regulatory requirement with monthly attestation reports completed by an independent certified public accounting firm.'
    },
    {
      icon: <FaShieldAlt className="text-4xl text-accent mb-4" />,
      title: 'Internal Control Examinations',
      description: 'Complete your controls examination to secure licenses, meet regulatory requirements, and deliver transparency to investors. Simplify the process by partnering with a crypto-native team of CPAs who truly understand your business needs.'
    },
    {
      icon: <FaLink className="text-4xl text-accent mb-4" />,
      title: 'On-Chain Proof of Reserves',
      description: 'In collaboration with Chainlink, bring off-chain reserve data from banks or custodians on chain for use in smart contracts, unlocking insurance products, circuit breaks, and other on-chain controls.'
    },
    {
      icon: <FaUserTie className="text-4xl text-accent mb-4" />,
      title: 'Outsourced Accounting & CFO',
      description: 'Streamline your financial operations with our crypto-native accounting and CFO services, providing expert insights, cost efficiency, and strategic guidance tailored to drive your business growth'
    },
    {
      icon: <FaClipboardCheck className="text-4xl text-accent mb-4" />,
      title: 'Compliance Readiness Services',
      description: 'Avoid suprises and streamline the completion of your financial audit, SOC 1/2, or licensing requirement by using The Audit Financier Team\'s experts to help guide you along the process.'
    },
    {
      icon: <FaChartLine className="text-4xl text-accent mb-4" />,
      title: 'Business Process & Operations Advisory',
      description: 'Implement crypto friendly systems. Scale your operations with policies, procedures and SOPs. Reduce overhead through operational efficiencies.'
    },
  ]

  const growthItems = [
    {
      title: 'Increase Credibility by Providing Transparency to Token Holders, Counterparties, DAO Treasuries and More',
      content: 'Build confidence with current and prospective users with industry-leading transparency.'
    },
    {
      title: 'Fulfill Your Regulatory Requirements',
      content: 'Navigate compliance with stringent regulators like MICA, VARA, NYDFS, BMA, and more.'
    },
    {
      title: 'Reduce Costs with Streamlined Processes, Controls, and Operations',
      content: 'Streamline your operations with automation, outsourcing, and cost reduction.'
    },
    {
      title: 'Support Fundraising & Due Diligence Efforts',
      content: 'Strengthen your fundraising efforts and simplify due duligence with trasnparency, demonstrable controls, and strong financial reporting.'
    },
  ]

  return (
    <>
      <Helmet>
        <title>RWA Attestation Services - Proof of Reserves Auditor</title>
        <meta name="description" content="Custom-tailored attestation solutions for Real-World Asset (RWA) token issuers. Real-time proof of reserves and compliance services." />
      </Helmet>

      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-primary-dark to-primary text-white py-20 overflow-hidden">
        {/* Animated Particle Wave Background */}
        <div className="absolute inset-0 opacity-30">
          <svg className="absolute bottom-0 w-full h-auto" viewBox="0 0 1280 400" preserveAspectRatio="none">
            <defs>
              <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#4A9BFF" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#4A9BFF" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#4A9BFF" stopOpacity="0.3" />
              </linearGradient>
            </defs>

            {/* Multiple wave layers for depth */}
            <path
              d="M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
              fill="url(#waveGradient)"
              opacity="0.4"
            >
              <animate
                attributeName="d"
                dur="8s"
                repeatCount="indefinite"
                values="
                  M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z;
                  M0,200 Q320,250 640,200 T1280,200 L1280,400 L0,400 Z;
                  M0,200 Q320,150 640,200 T1280,200 L1280,400 L0,400 Z"
              />
            </path>

            <path
              d="M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
              fill="url(#waveGradient)"
              opacity="0.3"
            >
              <animate
                attributeName="d"
                dur="10s"
                repeatCount="indefinite"
                values="
                  M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z;
                  M0,240 Q320,290 640,240 T1280,240 L1280,400 L0,400 Z;
                  M0,240 Q320,190 640,240 T1280,240 L1280,400 L0,400 Z"
                />
              </path>

            {/* Dotted particle effect */}
            <g opacity="0.6">
              {[...Array(50)].map((_, i) => {
                const x = (i * 25.6) % 1280;
                const y = 150 + (i * 13) % 200;
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r="2"
                    fill="#4A9BFF"
                    opacity="0.5"
                  >
                    <animate
                      attributeName="opacity"
                      values="0.3;0.8;0.3"
                      dur={`${3 + (i % 4)}s`}
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="cy"
                      values={`${y};${y - 20};${y}`}
                      dur={`${4 + (i % 3)}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                )
              })}
            </g>
          </svg>
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Real-World Asset (RWA)
              <br />
              <span className="text-accent">Attestation Services</span>
            </h1>
            <p className="text-2xl text-gray-200 mb-8">
              Custom-tailored solutions for RWA issuers
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-3">
                <FaCheck className="text-accent mt-1 flex-shrink-0" />
                <p className="text-lg">
                  Differentiate from Competitors by using Transparency as a Competitive Advantage
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <FaCheck className="text-accent mt-1 flex-shrink-0" />
                <p className="text-lg">
                  Optimize Operations, Bolster Processes, and Fulfill Compliance Requirements
                </p>
              </div>
              <div className="flex items-start space-x-3">
                <FaCheck className="text-accent mt-1 flex-shrink-0" />
                <p className="text-lg">
                  Establish Credibility with Counterparties across DeFi, CeFi, and TradFi
                </p>
              </div>
            </div>

            <Link
              to="/contact"
              className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              TALK TO AN EXPERT
            </Link>
          </div>
        </div>
      </section>

      {/* Trusted By Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-accent mb-12">
            Trusted by RWA Issuers Globally
          </h2>
          
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {trustedBy.map((company, index) => (
              <div key={index} className="flex items-center justify-center p-4">
                <div className="text-3xl font-bold text-gray-800 hover:text-primary transition-colors">
                  {company.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiate Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                <span className="text-accent">Differentiate</span> by using{' '}
                <span className="underline decoration-accent">Transparency</span> as a{' '}
                <span className="text-accent">Competitive Advantage</span>
              </h2>
              <div className="w-32 h-32 bg-primary rounded-full flex items-center justify-center mx-auto md:mx-0">
                <FaClock className="text-6xl text-white" />
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="flex items-start space-x-3">
                <FaCheck className="text-accent mt-1 flex-shrink-0" />
                <p className="text-lg">
                  Reduce your RWA attestation reporting frequency from every 30 days to every 30 seconds with{' '}
                  <Link to="/services/real-time-proof-of-reserves" className="text-accent hover:underline font-semibold">
                    Real-Time Proof of Reserves
                  </Link>
                  .
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <FaCheck className="text-accent mt-1 flex-shrink-0" />
                <p className="text-lg">
                  Bring off-chain reserve data on chain for use in smart contracts with{' '}
                  <Link to="/services/on-chain-proof-of-reserves" className="text-accent hover:underline font-semibold">
                    On-chain Proof of Reserves
                  </Link>
                  . On-Chain controls, insurance contracts, circuit breakers... with new tech comes new possibilities.
                </p>
              </div>
              
              <div className="flex items-start space-x-3">
                <FaCheck className="text-accent mt-1 flex-shrink-0" />
                <p className="text-lg">
                  Use Cryptocurrency Standard, Stablecoin Standard, AICPA, or custom criteria to report on unique aspects of your stablecoin operations to garner trust from your current and prospective customers.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Unlock Your Next Stage of Growth - Accordion Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-4xl font-bold text-center text-accent mb-12">
            Unlock Your Next Stage of Growth
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-4">
            {growthItems.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleAccordion(index)}
                  className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-lg font-semibold text-gray-900">{item.title}</span>
                  {openAccordion === index ? (
                    <FaMinus className="text-accent flex-shrink-0" />
                  ) : (
                    <FaPlus className="text-accent flex-shrink-0" />
                  )}
                </button>
                
                {openAccordion === index && (
                  <div className="px-6 pb-6 text-gray-700">
                    <p>{item.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Tailored Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-4xl font-bold text-center text-accent mb-12">
            Services Tailored for RWA Issuers
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {services.map((service, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow">
                {service.icon}
                <h3 className="text-xl font-bold mb-3 text-primary">{service.title}</h3>
                <p className="text-gray-600 text-sm">{service.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/contact"
              className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              TALK TO AN EXPERT
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <h2 className="text-4xl font-bold text-center text-accent mb-12">
            Don't Take Our Word for It
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-lg p-8 md:p-12 flex flex-col md:flex-row gap-8 items-center">
              <div className="flex-shrink-0">
                <div className="w-32 h-32 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                  <div className="text-4xl text-gray-600">👤</div>
                </div>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-4">
                  Nizar Tabet, Kitco, CFO
                </p>
                <blockquote className="text-2xl md:text-3xl font-bold text-primary">
                  "Thankful for the most efficient auditors I have ever worked with."
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary to-primary-dark text-white">
        <div className="container-custom text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-accent">
            Take Control of Your Future Today
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            <span className="font-semibold">Book a free call with one of our experts now</span> and discover how Audit Financier can help you unlock your next phase of growth.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
          >
            TALK TO THE EXPERTS
          </Link>
        </div>
      </section>
    </div>
    </>
  )
}

export default RWATokenIssuers